/**
 * Diagnóstico da falha de envio — os campos ESTRUTURADOS que fazem o alerta dizer "o que foi".
 *
 * Por que existe: "envio falhou · motivo: Error: Bad Request (status 400)" não deixa a operação
 * decidir nada. Ela precisa saber em que ETAPA quebrou, se a conversa estava MESMO encerrada, e se
 * dois alertas são o MESMO evento. O backend aceita estes campos por allowlist de valores e os monta
 * antes do texto livre — porque o truncamento comia justamente o código do erro.
 *
 * 🔴 Este módulo vai para o BUNDLE. Nada de API de Node (`crypto`, `path`, `os`) — nem transitiva.
 * O incidente de 28/07 (widget sumindo por ~1h20) foi exatamente um `require('path')` escondido.
 *
 * Tudo aqui é best-effort: nenhuma função lança, e a consulta ao Twilio tem timeout curto porque
 * roda dentro do `catch` do envio — telemetria não pode segurar o atendimento.
 */

import { temCaraDeDocumento } from "./descreverErro";

/*
 * Etapa em que a falha parou. As quatro primeiras são do pipeline de ENVIO; a última é da
 * ABERTURA da sessão — foram criadas porque, com o corte do texto livre, timeout do handshake e
 * falha de lookup da conversa chegavam idênticos à operação ("Error: falha técnica"), e são três
 * investigações completamente diferentes: Twilsock, token ou SID inexistente.
 */
export type EtapaDaFalha =
    | "media_prepare"
    | "mcs_upload"
    | "message_create"
    | "local_effect"
    | "conversation_lookup";
export type EstadoDaConversa = "active" | "inactive" | "closed";
export type DesfechoDaConsulta = "ok" | "timeout" | "failed" | "unavailable";

export interface ConsultaDoEstadoReal {
    conversationStateRemote?: EstadoDaConversa;
    lookupOutcome: DesfechoDaConsulta;
}

/** O mínimo do client do SDK que a consulta usa — estrutural, para não acoplar ao tipo do Twilio. */
export interface ClientConsultavel {
    getConversationBySid?: (sid: string) => Promise<{ state?: { current?: string } } | undefined>;
}

const ESTADOS: readonly EstadoDaConversa[] = ["active", "inactive", "closed"];
const TIMEOUT_PADRAO_MS = 1500;

/** Correlação, não segurança: hex curto o bastante para casar dois alertas do mesmo evento. */
export const gerarReportId = (): string => {
    let hex = "";
    while (hex.length < 8) {
        hex += Math.floor(Math.random() * 16).toString(16);
    }
    return hex;
};

/**
 * A etapa, decidida pela FORMA do erro — não pela posição no código.
 *
 * O MCS (upload de mídia) monta a mensagem como "<status>: [<statusText>] …" e lança
 * `TransportError` (mcs-client/transport.js:76-107); o envio da mensagem falha com `status` HTTP ou
 * `TwilsockUpstreamError`. `media_prepare` e `local_effect` NÃO são inferíveis pela forma — exigem
 * separar o `try` do envio (PR própria) — então o campo é OMITIDO em vez de chutado.
 */
export const classificarEtapa = (erro: unknown): EtapaDaFalha | undefined => {
    if (!erro || typeof erro !== "object") {
        return undefined;
    }
    const e = erro as { name?: unknown; message?: unknown; status?: unknown };
    const nome = typeof e.name === "string" ? e.name : "";
    const mensagem = typeof e.message === "string" ? e.message : "";
    if (nome === "TransportError" || /^\d+:\s*\[/.test(mensagem)) {
        return "mcs_upload";
    }
    if (nome === "TwilsockUpstreamError" || typeof e.status === "number" || typeof e.status === "string") {
        return "message_create";
    }
    return undefined;
};

/**
 * 🔒 Forma de código E sem cara de documento — as DUAS, e a segunda foi esquecida até 2026-09-07.
 *
 * O campo estruturado sai em claro no alerta: a redação do backend alcança o `reason`, não isto. Um
 * CPF de 11 dígitos casa com "alfanumérico curto" e passava inteiro. A trava é a MESMA do sufixo do
 * motivo, importada em vez de recopiada — duas cópias da regra é como um dos destinos fica para trás.
 */
const codigoCurto = (valor: unknown): string | undefined => {
    const texto = typeof valor === "number" ? String(valor) : typeof valor === "string" ? valor.trim() : "";
    if (!/^[a-z0-9_-]{1,12}$/i.test(texto) || temCaraDeDocumento(texto)) {
        return undefined;
    }
    return texto;
};

/**
 * Código e status do fornecedor como campos separados — para sobreviverem ao truncamento do motivo.
 *
 * 🔴 DOIS FORMATOS, de propósito. O `Error` de rede traz `code`/`status`; o objeto que o SDK do
 * Twilio LANÇA traz `errorCode`/`httpStatusCode`. Ler só o primeiro par deixava o alerta mais
 * frequente da produção sem código nenhum — medido nos alertas de 04 a 06/09/2026, onde
 * `object lançado {terminal, message, httpStatusCode, errorCode}` chegava mudo e centenas de
 * ocorrências ficavam indistinguíveis entre token, permissão e rede.
 *
 * `code`/`status` têm precedência porque são a forma que os caminhos existentes já reportavam.
 * Os valores passam pelo `codigoCurto`: número ou código curto entra, texto livre não — a `message`
 * do fornecedor não é nossa para garantir que não carrega dado de apostador.
 */
export const extrairCodigos = (erro: unknown): { errorCode?: string; errorStatus?: string } => {
    if (!erro || typeof erro !== "object") {
        return {};
    }
    const e = erro as { code?: unknown; status?: unknown; errorCode?: unknown; httpStatusCode?: unknown };
    const errorCode = codigoCurto(e.code) ?? codigoCurto(e.errorCode);
    const errorStatus = codigoCurto(e.status) ?? codigoCurto(e.httpStatusCode);
    return { ...(errorCode ? { errorCode } : {}), ...(errorStatus ? { errorStatus } : {}) };
};

/**
 * Pergunta ao Twilio o estado REAL da conversa — diagnóstico, não decisão de tela.
 *
 * Distingue "conversa ativa" (`ok` + estado) de "não consegui verificar" (`timeout`/`failed`/
 * `unavailable`): colapsar os dois num `false` foi o defeito que a revisão apontou, porque
 * "não sei" passaria por "está ativa". Nunca lança; o timer é limpo em qualquer desfecho.
 */
export const consultarEstadoReal = async (
    client: ClientConsultavel | undefined,
    sid: string | undefined,
    timeoutMs: number = TIMEOUT_PADRAO_MS
): Promise<ConsultaDoEstadoReal> => {
    if (!client?.getConversationBySid || !sid) {
        return { lookupOutcome: "unavailable" };
    }
    let timer: ReturnType<typeof setTimeout> | undefined;
    const expirar = new Promise<"timeout">((resolve) => {
        timer = setTimeout(() => resolve("timeout"), timeoutMs);
    });
    try {
        const resultado = await Promise.race([client.getConversationBySid(sid), expirar]);
        if (resultado === "timeout") {
            return { lookupOutcome: "timeout" };
        }
        const estado = (resultado as { state?: { current?: string } } | undefined)?.state?.current;
        return ESTADOS.includes(estado as EstadoDaConversa)
            ? { conversationStateRemote: estado as EstadoDaConversa, lookupOutcome: "ok" }
            : { lookupOutcome: "ok" };
    } catch {
        return { lookupOutcome: "failed" };
    } finally {
        if (timer) {
            clearTimeout(timer);
        }
    }
};
